import Circle from '@rio-cloud/rio-uikit/Circle';
import EventUtils, { type EventListenerMap } from '@rio-cloud/rio-uikit/EventUtils';
import Marker from '@rio-cloud/rio-uikit/Marker';
import type { MapApi, MapEvent } from '@rio-cloud/rio-uikit/mapTypes';
import Polygon, { colorMapMarker } from '@rio-cloud/rio-uikit/Polygon';
import { Fragment } from 'react';
import { connect } from 'react-redux';
import { getFormValues } from 'redux-form';

import {
    dataLayerPush,
    GAEventName,
    GAEventTrigger,
    GAFlowName,
} from '../../configuration/googleAnalytics/googleAnalytics';
import type { State } from '../../reducers';
import type { Dispatch } from '../../types';
import { dragGeofencePoint } from '../actions/mapActions';
import { geofenceFormElements } from '../components/geofence/GeofenceForm';
import { getEditMode } from '../reducers/selectors';
import type { Coordinates } from '../types';
import { GeofencePointMarker } from './Markers';
import { geofenceBottomSheetActions } from './redux/geofenceBottomSheet.reducer';

const STROKE_COLOR = colorMapMarker['color-map-marker-geofence'].stroke;
const FILL_COLOR = colorMapMarker['color-map-marker-geofence'].fill;

type CreationFormFormValues = {
    points: Coordinates[];
    activateGeofence: boolean;
    geofenceAddress: string;
    geofenceRadius: number;
    geofenceShape: 'circular' | 'polygonal';
    geofenceName: string;
    suggestionPoint: Coordinates;
};

type CircularGeofence = {
    name: string;
    type: 'circular';
    center: Coordinates;
    radius: number;
};

type PolygonalGeofence = {
    name: string;
    type: 'polygonal';
    points: Coordinates[];
};

type Geofence = CircularGeofence | PolygonalGeofence;

export type MapGeofenceRendererProps = {
    editMode: boolean;
    geofence?: Geofence;
    onDrag: (point: Coordinates, index: number) => void;
    mapApi: MapApi;
    markerEventListenerMap: EventListenerMap;
};

export const MapGeofenceRenderer = (props: MapGeofenceRendererProps) => {
    const { editMode, geofence, onDrag, mapApi, markerEventListenerMap } = props;

    let dragStart: Coordinates | undefined;
    let dragPoint: Coordinates | undefined;

    if (!geofence) {
        return null;
    }

    if (geofence.type === 'polygonal') {
        const eventListenerMap = {
            [EventUtils.DRAG_START]: (event: Event, enhancedEvent: MapEvent) => {
                const target: any = event.target;
                const index = target.data.id;
                dragStart = enhancedEvent.calculateGeoCoords();
                dragPoint = geofence.points[index];
                mapApi.behavior.disable();
            },
            [EventUtils.DRAG_END]: (event: Event, enhancedEvent: MapEvent) => {
                if (!dragStart || !dragPoint) {
                    return;
                }
                const target: any = event.target;
                const index = target.data.id;
                const dragTo = enhancedEvent.calculateGeoCoords();
                const point = {
                    lat: dragPoint.lat - dragStart.lat + dragTo.lat,
                    lng: dragPoint.lng - dragStart.lng + dragTo.lng,
                };
                dataLayerPush({
                    trigger: GAEventTrigger.click,
                    event: GAEventName.polygonalGeofenceMarkerDragged,
                    flow_name: GAFlowName.createGeofence,
                });
                onDrag(point, index);
                mapApi.behavior.enable();
            },
            [EventUtils.DRAG]: (event: Event, ev: MapEvent) => {
                const target: any = event.target;
                target.setGeometry(mapApi.map.screenToGeo(ev.viewportX, ev.viewportY));
            },
        };

        return (
            <Fragment>
                <Polygon points={geofence.points} style={{ strokeColor: STROKE_COLOR, fillColor: FILL_COLOR }} />
                {!editMode &&
                    geofence.points.map((point, index) => (
                        <Marker
                            key={index}
                            position={point}
                            icon={<GeofencePointMarker showDragCursor={true} />}
                            customData={{ id: index }}
                            draggable={true}
                            eventListenerMap={eventListenerMap}
                        />
                    ))}
            </Fragment>
        );
    }

    if (geofence.type === 'circular') {
        return (
            <Fragment>
                <Marker
                    position={geofence.center}
                    icon={<GeofencePointMarker />}
                    draggable={!editMode}
                    eventListenerMap={markerEventListenerMap}
                />
                <Circle
                    position={geofence.center}
                    radius={geofence.radius}
                    style={{ strokeColor: STROKE_COLOR, fillColor: FILL_COLOR }}
                    precision={30}
                />
            </Fragment>
        );
    }
    return null;
};

export const mapStateToProps = (state: State) => {
    const formValues = getFormValues(geofenceFormElements.geofenceFormName)(state)
        ? (getFormValues(geofenceFormElements.geofenceFormName)(state) as CreationFormFormValues)
        : ({} as CreationFormFormValues);

    const getGeofence: () => Geofence | undefined = () =>
        formValues.geofenceShape === 'circular' && formValues.points && formValues.points.length > 0
            ? {
                  type: 'circular',
                  center: formValues.points[0],
                  radius: formValues.geofenceRadius ? formValues.geofenceRadius : 0,
                  name: formValues.geofenceName ? formValues.geofenceName : '',
              }
            : formValues.geofenceShape === 'polygonal' && formValues.points && formValues.points.length > 0
              ? {
                    type: 'polygonal',
                    points: formValues.points,
                    name: formValues.geofenceName ? formValues.geofenceName : '',
                }
              : undefined;

    return {
        editMode: getEditMode(state) === 'UPDATE',
        geofence: getGeofence(),
    };
};

export const mapDispatchToProps = (dispatch: Dispatch) => ({
    onDrag: (point: Coordinates, index: number) => {
        dispatch(dragGeofencePoint(point, index));
        dispatch(geofenceBottomSheetActions.hideGeofenceBottomSheet());
    },
});

const ConnectedMapGeofenceRenderer = connect(mapStateToProps, mapDispatchToProps)(MapGeofenceRenderer);
ConnectedMapGeofenceRenderer.displayName = 'ConnectedMapGeofenceRenderer';

export default ConnectedMapGeofenceRenderer;
